import type { AxiosError } from 'axios';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';

interface ServerError {
	validation?: Record<string, string>;
	msg?: string;
	/** ProblemDetails `detail` — the specific message of `ValidationProblem(detail)` responses. */
	detail?: string;
	/** ProblemDetails `errors` — `ValidationProblem(ModelState)` puts the specific messages here, not in `detail`. */
	errors?: Record<string, string[] | string>;
	title?: string;
	/** A refusal's localized text (`BookingRefusal { code, message }`, BW-8457 D13). */
	message?: string;
}

/**
 * Maps a 400 `validation: { FieldPath: message }` body onto RHF field errors. Mirrors the
 * per-editor helpers in the Promotion/Product editors, hoisted to a shared util so the
 * System CRUD pages (Languages/Countries/Users/…) don't each re-implement it. Server keys
 * are PascalCase token fields; RHF fields are camelCase (best-effort lowercase of the head).
 * Returns true when at least one field error was applied.
 */
export function applyServerValidation<T extends FieldValues>(err: unknown, setError: UseFormSetError<T>): boolean {
	const validation = (err as AxiosError<ServerError>)?.response?.data?.validation;
	if (!validation || typeof validation !== 'object') return false;
	let handled = false;
	for (const [key, message] of Object.entries(validation)) {
		const field = (key.charAt(0).toLowerCase() + key.slice(1)) as Path<T>;
		setError(field, { message: String(message) });
		handled = true;
	}
	return handled;
}

/** Flattens ProblemDetails `errors` into one sentence, or undefined when it carries no message. */
function problemErrors(errors: ServerError['errors']): string | undefined {
	if (!errors || typeof errors !== 'object') return undefined;
	const messages = Object.values(errors).flat().filter(Boolean);
	return messages.length ? messages.join(' ') : undefined;
}

/** Best-effort human message from an Axios error (server `msg`/`message`/`detail`/`errors`/`title`, else the axios message). */
export function serverMessage(err: unknown): string {
	const ax = err as AxiosError<ServerError>;
	// Some endpoints return a plain-string body (e.g. BadRequest(ex.Message)) rather than the {msg}/{title} shape.
	const data = ax?.response?.data as unknown;
	if (typeof data === 'string' && data.trim()) return data;
	const body = ax?.response?.data;
	return body?.msg ?? body?.message ?? body?.detail ?? problemErrors(body?.errors) ?? body?.title ?? ax?.message ?? 'Error';
}
